import {
  getScrollContainer,
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const STERLING_STORY_QUERY = window.matchMedia('(min-width: 990px) and (min-height: 700px)');
const STERLING_PROJECT_STORY_QUERY = window.matchMedia(
  '(min-width: 990px) and (min-height: 700px), (max-width: 650px)'
);
const STERLING_PROJECT_MOBILE_QUERY = window.matchMedia('(max-width: 650px)');
const STERLING_FEATURE_STORY_QUERY = window.matchMedia(
  '(min-width: 990px) and (min-height: 700px), (max-width: 650px)'
);
const STERLING_FEATURE_MOBILE_QUERY = window.matchMedia('(max-width: 650px)');
const STERLING_HORIZONTAL_STORY_QUERY = window.matchMedia('(max-width: 650px)');
const STERLING_REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
const STERLING_STORY_HYSTERESIS = 24;

class SterlingScrollStory {
  constructor(host, { frame, count, getIndex, select, mediaQuery = STERLING_STORY_QUERY, getPacing }) {
    this.host = host;
    this.frame = frame;
    this.count = count;
    this.getIndex = getIndex;
    this.select = select;
    this.mediaQuery = mediaQuery;
    this.getPacing = getPacing;
    this.events = new AbortController();
    this.updateFrame = null;
    this.measureFrame = null;
    this.scrollTarget = null;
    this.active = false;

    this.scheduleUpdate = () => {
      if (this.updateFrame !== null || !this.active) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    };

    this.scheduleMeasure = () => {
      if (this.measureFrame !== null) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    };

    this.refresh = () => {
      const shouldActivate = Boolean(
        this.frame && this.count > 1 && this.mediaQuery.matches && !STERLING_REDUCED_MOTION_QUERY.matches
      );
      if (!shouldActivate) {
        this.deactivate();
        return;
      }

      this.active = true;
      this.host.dataset.scrollStory = 'active';
      this.host.dataset.scrollStoryMode = this.getPacing?.().mode || 'desktop';
      this.bindScrollTarget();
      this.scheduleMeasure();
    };

    const options = { signal: this.events.signal };
    window.addEventListener('resize', this.scheduleMeasure, options);
    this.mediaQuery.addEventListener('change', this.refresh, options);
    STERLING_REDUCED_MOTION_QUERY.addEventListener('change', this.refresh, options);
    scrollContainerMediaQuery.addEventListener('change', this.refresh, options);
    window.visualViewport?.addEventListener('resize', this.scheduleMeasure, options);
    this.refresh();
  }

  bindScrollTarget() {
    const nextTarget = getScrollEventTarget();
    if (nextTarget === this.scrollTarget) return;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = nextTarget;
    this.scrollTarget.addEventListener('scroll', this.scheduleUpdate, { passive: true });
  }

  measure() {
    if (!this.active) return;

    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const pacing = this.getPacing?.() || { minimum: 280, maximum: 380, ratio: 0.38, mode: 'desktop' };
    this.host.dataset.scrollStoryMode = pacing.mode;
    this.perStep = Math.min(pacing.maximum, Math.max(pacing.minimum, viewportHeight * pacing.ratio));
    this.distance = this.perStep * this.count;

    const frameHeight = this.frame.offsetHeight;
    this.host.style.setProperty('--sterling-story-track-height', `${frameHeight + this.distance}px`);

    const scrollContainer = getScrollContainer();
    this.rootTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
    const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 72;
    this.stickyTop = Number.parseFloat(getComputedStyle(this.frame).top) || headerHeight + 20;
    this.start = getScrollTop() + this.host.getBoundingClientRect().top - this.rootTop - this.stickyTop;
    this.update();
  }

  update() {
    if (!this.active || !Number.isFinite(this.start)) return;

    const hostTop = this.host.getBoundingClientRect().top - this.rootTop;
    if (hostTop > this.stickyTop + 1) {
      this.start = getScrollTop() + hostTop - this.stickyTop;
    }

    const progress = Math.min(this.distance, Math.max(0, getScrollTop() - this.start));
    let nextIndex = this.getIndex();

    while (
      nextIndex < this.count - 1 &&
      progress >= (nextIndex + 1) * this.perStep + STERLING_STORY_HYSTERESIS
    ) {
      nextIndex += 1;
    }
    while (
      nextIndex > 0 &&
      progress < nextIndex * this.perStep - STERLING_STORY_HYSTERESIS
    ) {
      nextIndex -= 1;
    }

    if (nextIndex !== this.getIndex()) this.select(nextIndex);
  }

  deactivate() {
    this.active = false;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = null;
    this.host.removeAttribute('data-scroll-story');
    this.host.removeAttribute('data-scroll-story-mode');
    this.host.style.removeProperty('--sterling-story-track-height');
    if (this.updateFrame !== null) cancelAnimationFrame(this.updateFrame);
    if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
    this.updateFrame = null;
    this.measureFrame = null;
  }

  destroy() {
    this.deactivate();
    this.events.abort();
  }
}

class SterlingHorizontalStory extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.frame = this.querySelector('[data-sterling-horizontal-frame]');
    this.rail = this.querySelector('[data-sterling-horizontal-rail]');
    this.items = this.rail ? [...this.rail.children] : [];
    this.dots = [...this.querySelectorAll('[data-sterling-horizontal-dot]')];
    this.scrollTarget = null;
    this.updateFrame = null;
    this.measureFrame = null;
    this.railFrame = null;
    this.programmaticFrame = null;
    this.anchor = null;
    this.active = false;
    this.settingRailScroll = false;

    this.scheduleUpdate = () => {
      if (!this.active || this.updateFrame !== null) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    };

    this.scheduleMeasure = () => {
      if (this.measureFrame !== null) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    };

    this.scheduleRailUpdate = () => {
      if (this.railFrame !== null) return;
      this.railFrame = requestAnimationFrame(() => {
        this.railFrame = null;
        this.updateDots();
        if (!this.active || this.settingRailScroll || !Number.isFinite(this.start)) return;
        this.anchor = {
          progress: this.getProgress(),
          position: this.rail.scrollLeft,
        };
      });
    };

    this.refresh = () => {
      const shouldActivate = Boolean(
        this.frame && this.rail && this.items.length > 1 &&
        STERLING_HORIZONTAL_STORY_QUERY.matches && !STERLING_REDUCED_MOTION_QUERY.matches
      );

      if (!shouldActivate) {
        this.deactivate();
        this.updateDots();
        return;
      }

      this.active = true;
      this.dataset.scrollStory = 'active';
      this.bindScrollTarget();
      this.scheduleMeasure();
    };

    this.rail?.addEventListener('scroll', this.scheduleRailUpdate, { passive: true, signal });
    window.addEventListener('resize', this.scheduleMeasure, { signal });
    window.visualViewport?.addEventListener('resize', this.scheduleMeasure, { signal });
    STERLING_HORIZONTAL_STORY_QUERY.addEventListener('change', this.refresh, { signal });
    STERLING_REDUCED_MOTION_QUERY.addEventListener('change', this.refresh, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.refresh, { signal });
    this.refresh();
  }

  bindScrollTarget() {
    const nextTarget = getScrollEventTarget();
    if (nextTarget === this.scrollTarget) return;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = nextTarget;
    this.scrollTarget.addEventListener('scroll', this.scheduleUpdate, { passive: true });
  }

  measure() {
    if (!this.rail) return;

    this.style.removeProperty('--sterling-horizontal-story-height');
    this.overflow = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    this.updateDots();
    if (!this.active || this.overflow < 1) {
      if (this.overflow < 1) this.deactivate();
      return;
    }

    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.holdDistance = Math.min(140, Math.max(96, viewportHeight * 0.14));
    this.distance = this.overflow + this.holdDistance;
    this.travelDistance = this.overflow;
    this.anchor = null;

    const naturalHeight = this.scrollHeight;
    this.style.setProperty('--sterling-horizontal-story-height', `${naturalHeight + this.distance}px`);

    const scrollContainer = getScrollContainer();
    this.rootTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
    this.stickyTop = Number.parseFloat(getComputedStyle(this.frame).top) || 80;
    this.start = getScrollTop() + this.frame.getBoundingClientRect().top - this.rootTop - this.stickyTop;
    this.update();
  }

  getProgress() {
    return Math.min(this.distance || 0, Math.max(0, getScrollTop() - this.start));
  }

  getHorizontalPosition(progress) {
    if (!this.anchor) return Math.min(this.overflow, progress);

    const anchorProgress = Math.min(this.travelDistance, this.anchor.progress);
    const anchorPosition = Math.min(this.overflow, Math.max(0, this.anchor.position));
    if (progress <= anchorProgress) {
      if (anchorProgress < 1) return anchorPosition;
      return anchorPosition * (progress / anchorProgress);
    }
    if (progress >= this.travelDistance) return this.overflow;

    const remainingProgress = this.travelDistance - anchorProgress;
    const ratio = remainingProgress > 0 ? (progress - anchorProgress) / remainingProgress : 1;
    return anchorPosition + (this.overflow - anchorPosition) * ratio;
  }

  update() {
    if (!this.active || !Number.isFinite(this.start) || !this.rail) return;

    const frameTop = this.frame.getBoundingClientRect().top - this.rootTop;
    if (frameTop > this.stickyTop + 1) {
      this.start = getScrollTop() + frameTop - this.stickyTop;
      if (this.anchor) this.anchor.progress = 0;
    }

    const position = this.getHorizontalPosition(this.getProgress());
    this.settingRailScroll = true;
    this.rail.scrollLeft = position;
    this.updateDots();
    if (this.programmaticFrame !== null) cancelAnimationFrame(this.programmaticFrame);
    this.programmaticFrame = requestAnimationFrame(() => {
      this.programmaticFrame = null;
      this.settingRailScroll = false;
    });
  }

  updateDots() {
    if (!this.rail || !this.items.length || !this.dots.length) return;
    const firstOffset = this.items[0].offsetLeft;
    let activeIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - firstOffset - this.rail.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    });

    this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
  }

  deactivate() {
    this.active = false;
    this.anchor = null;
    this.scrollTarget?.removeEventListener('scroll', this.scheduleUpdate);
    this.scrollTarget = null;
    this.removeAttribute('data-scroll-story');
    this.style.removeProperty('--sterling-horizontal-story-height');
    if (this.updateFrame !== null) cancelAnimationFrame(this.updateFrame);
    if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
    if (this.programmaticFrame !== null) cancelAnimationFrame(this.programmaticFrame);
    this.updateFrame = null;
    this.measureFrame = null;
    this.programmaticFrame = null;
    this.settingRailScroll = false;
  }

  disconnectedCallback() {
    this.deactivate();
    if (this.railFrame !== null) cancelAnimationFrame(this.railFrame);
    this.railFrame = null;
    this.abortController?.abort();
  }
}

class SterlingHomeHero extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-sterling-hero-video]');
    if (!this.video) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleMotionChange = () => {
      if (this.motionQuery.matches) {
        this.video.pause();
        return;
      }

      this.video.play().catch(() => {});
    };

    this.motionQuery.addEventListener?.('change', this.handleMotionChange);
    this.handleMotionChange();
  }

  disconnectedCallback() {
    this.motionQuery?.removeEventListener?.('change', this.handleMotionChange);
    this.video?.pause();
  }
}

class SterlingBattlefield extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.panels = [...this.querySelectorAll('[data-sterling-panel]')];
    this.directLinks = this.hasAttribute('data-direct-panel-links');
    this.panels.forEach((panel) => {
      if (this.directLinks) {
        const toggle = panel.querySelector('[data-sterling-panel-toggle]');
        if (toggle) toggle.hidden = false;
        panel.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          if (!window.matchMedia('(min-width: 901px) and (hover: hover)').matches) return;
          const focusedPanel = document.activeElement?.closest?.('[data-sterling-panel]');
          if (focusedPanel && focusedPanel !== panel && this.contains(focusedPanel)) return;
          this.openPanel(panel);
        }, { signal });
        panel.addEventListener('focusin', (event) => {
          // Disclosure buttons open on activation, not on the focus before a tap.
          if (!event.target.closest('[data-sterling-panel-toggle]')) this.openPanel(panel);
        }, { signal });
        panel.addEventListener('focusout', () => queueMicrotask(() => {
          if (!this.isConnected || this.escapeClosing || this.contains(document.activeElement)) return;
          const hoveredPanel = this.panels.find((item) => item.matches(':hover'));
          if (hoveredPanel) this.openPanel(hoveredPanel);
          else this.closePanel(panel);
        }), { signal });
      }
      panel.querySelector('[data-sterling-panel-toggle]')?.addEventListener(
        'click',
        () => this.togglePanel(panel),
        { signal }
      );

      panel.addEventListener(
        'pointerleave',
        (event) => {
          // Touch pointers leave on finger lift; keep the panel open for its link tap.
          if (event.pointerType !== 'mouse') return;
          if (!panel.contains(document.activeElement)) this.closePanel(panel);
        },
        { signal }
      );
    });

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const activePanel = this.querySelector('[data-sterling-panel].is-active');
        if (!activePanel) return;
        const toggle = activePanel.querySelector('[data-sterling-panel-toggle]');
        if (this.directLinks) {
          this.escapeClosing = true;
          this.focus();
          this.closePanel(activePanel);
          queueMicrotask(() => { this.escapeClosing = false; });
          return;
        }
        this.closePanel(activePanel);
        toggle?.focus();
      },
      { signal }
    );

    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const selectedPanel = event.target.closest?.('[data-sterling-panel]');
        if (selectedPanel && this.contains(selectedPanel)) this.openPanel(selectedPanel);
      },
      { signal }
    );

    this.addEventListener(
      'shopify:block:deselect',
      (event) => {
        const selectedPanel = event.target.closest?.('[data-sterling-panel]');
        if (selectedPanel && this.contains(selectedPanel)) this.closePanel(selectedPanel);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  togglePanel(panel) {
    const shouldOpen = !panel.classList.contains('is-active');
    this.panels.forEach((candidate) => this.closePanel(candidate));
    if (shouldOpen) this.openPanel(panel);
  }

  openPanel(panel) {
    this.panels.forEach((candidate) => {
      if (candidate !== panel) this.closePanel(candidate);
    });
    panel.classList.add('is-active');
    panel.querySelector('[data-sterling-panel-toggle]')?.setAttribute('aria-expanded', 'true');
    panel.querySelector('[data-sterling-panel-details]')?.setAttribute('aria-hidden', 'false');
  }

  closePanel(panel) {
    panel.classList.remove('is-active');
    panel.querySelector('[data-sterling-panel-toggle]')?.setAttribute('aria-expanded', 'false');
    panel.querySelector('[data-sterling-panel-details]')?.setAttribute('aria-hidden', 'true');
  }
}

class SterlingProjectFinder extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs = [...this.querySelectorAll('[data-sterling-project-tab]')];
    this.panels = [...this.querySelectorAll('[data-sterling-project-panel]')];
    this.currentIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
    this.scrollStory?.destroy();

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), { signal });
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index), { signal });
    });

    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const panel = event.target.closest?.('[data-sterling-project-panel]');
        if (!panel || !this.contains(panel)) return;
        this.select(Number(panel.dataset.index), { focus: false });
      },
      { signal }
    );

    this.scrollStory = new SterlingScrollStory(this, {
      frame: this.querySelector('[data-sterling-story-frame]'),
      count: this.tabs.length,
      getIndex: () => this.currentIndex,
      select: (index) => this.select(index),
      mediaQuery: STERLING_PROJECT_STORY_QUERY,
      getPacing: () =>
        STERLING_PROJECT_MOBILE_QUERY.matches
          ? { minimum: 180, maximum: 250, ratio: 0.29, mode: 'mobile' }
          : { minimum: 280, maximum: 380, ratio: 0.38, mode: 'desktop' },
    });
  }

  disconnectedCallback() {
    this.scrollStory?.destroy();
    this.abortController?.abort();
  }

  handleTabKeydown(event, index) {
    const lastIndex = this.tabs.length - 1;
    let nextIndex;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.select(nextIndex, { focus: true });
  }

  select(index, { focus = false } = {}) {
    if (!this.tabs[index] || !this.panels[index]) return;
    this.currentIndex = index;

    this.tabs.forEach((tab, candidateIndex) => {
      const active = candidateIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, candidateIndex) => {
      const active = candidateIndex === index;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', String(!active));
      panel.toggleAttribute('inert', !active);
    });

    if (focus) this.tabs[index].focus();
  }
}

class SterlingFeatureShowcase extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.copies = [...this.querySelectorAll('[data-sterling-feature-copy]')];
    this.media = [...this.querySelectorAll('[data-sterling-feature-media]')];
    this.counter = this.querySelector('[data-sterling-feature-counter]');
    this.nextTitle = this.querySelector('[data-sterling-feature-next-title]');
    this.currentIndex = Math.max(0, this.copies.findIndex((copy) => copy.classList.contains('is-active')));
    this.scrollStory?.destroy();

    this.querySelector('[data-sterling-feature-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-sterling-feature-next]')?.addEventListener('click', () => this.step(1), { signal });

    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const copy = event.target.closest?.('[data-sterling-feature-copy]');
        if (!copy || !this.contains(copy)) return;
        this.select(Number(copy.dataset.index));
      },
      { signal }
    );

    this.select(this.currentIndex);
    this.scrollStory = new SterlingScrollStory(this, {
      frame: this.querySelector('[data-sterling-story-frame]'),
      count: this.copies.length,
      getIndex: () => this.currentIndex,
      select: (index) => this.select(index),
      mediaQuery: STERLING_FEATURE_STORY_QUERY,
      getPacing: () =>
        STERLING_FEATURE_MOBILE_QUERY.matches
          ? { minimum: 160, maximum: 220, ratio: 0.24, mode: 'mobile' }
          : { minimum: 280, maximum: 380, ratio: 0.38, mode: 'desktop' },
    });
  }

  disconnectedCallback() {
    this.scrollStory?.destroy();
    this.abortController?.abort();
  }

  step(direction) {
    const count = this.copies.length;
    if (!count) return;
    this.select((this.currentIndex + direction + count) % count);
  }

  select(index) {
    if (!this.copies[index] || !this.media[index]) return;
    this.currentIndex = index;

    this.copies.forEach((copy, candidateIndex) => {
      const active = candidateIndex === index;
      copy.classList.toggle('is-active', active);
      copy.setAttribute('aria-hidden', String(!active));
      copy.toggleAttribute('inert', !active);
    });

    this.media.forEach((item, candidateIndex) => item.classList.toggle('is-active', candidateIndex === index));

    const total = this.copies.length;
    const nextIndex = (index + 1) % total;
    if (this.counter) this.counter.textContent = `${this.twoDigits(index + 1)} / ${this.twoDigits(total)}`;
    if (this.nextTitle) {
      this.nextTitle.textContent = this.copies[nextIndex].querySelector('.sterling-home-feature-showcase__title')?.textContent?.trim() || '';
    }
  }

  twoDigits(value) {
    return String(value).padStart(2, '0');
  }
}

if (!customElements.get('sterling-home-hero')) {
  customElements.define('sterling-home-hero', SterlingHomeHero);
}

if (!customElements.get('sterling-battlefield')) {
  customElements.define('sterling-battlefield', SterlingBattlefield);
}

if (!customElements.get('sterling-project-finder')) {
  customElements.define('sterling-project-finder', SterlingProjectFinder);
}

if (!customElements.get('sterling-feature-showcase')) {
  customElements.define('sterling-feature-showcase', SterlingFeatureShowcase);
}

if (!customElements.get('sterling-horizontal-story')) {
  customElements.define('sterling-horizontal-story', SterlingHorizontalStory);
}
